import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, Spinner, Form, Table } from "react-bootstrap";
import {
  getTagDefinitions,
  getEntityTags,
  updateEntityTag,
  getTeachersBySchoolId,
} from "../services/APIClient";

const CHATBOT_TAG_NAME = "chatbot_teacher_access";

function TeacherChatbotAccessModal({ show, onHide, schoolId, schoolName, userName }) {
  const [teachers, setTeachers] = useState([]);
  const [tagDefinition, setTagDefinition] = useState(null);
  const [teacherTags, setTeacherTags] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [changedTags, setChangedTags] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (show && schoolId) {
      fetchData();
    }
  }, [show, schoolId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [teacherList, definitions] = await Promise.all([
        getTeachersBySchoolId(schoolId),
        getTagDefinitions(),
      ]);

      const chatbotDef = definitions.find((d) => d.tag_name === CHATBOT_TAG_NAME);
      if (!chatbotDef) {
        setError(
          `Tag definition '${CHATBOT_TAG_NAME}' not found. Please create it in Tag Management first.`
        );
        setLoading(false);
        return;
      }

      setTagDefinition(chatbotDef);
      setTeachers(teacherList);

      const tagResults = await Promise.all(
        teacherList.map(async (teacher) => {
          try {
            const tags = await getEntityTags("teacher", teacher.id);
            const tag = tags.find((t) => t.tag_definition_id === chatbotDef.id);
            return { teacherId: teacher.id, value: tag?.tag_value || "OFF" };
          } catch {
            return { teacherId: teacher.id, value: "OFF" };
          }
        })
      );

      const tagMap = {};
      tagResults.forEach(({ teacherId, value }) => {
        tagMap[teacherId] = value;
      });
      setTeacherTags(tagMap);
      setChangedTags({});
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (teacherId, checked) => {
    setChangedTags((prev) => ({ ...prev, [teacherId]: checked ? "ON" : "OFF" }));
    setSaveSuccess(false);
  };

  const getCurrentValue = (teacherId) => {
    if (changedTags.hasOwnProperty(teacherId)) return changedTags[teacherId];
    return teacherTags[teacherId] || "OFF";
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await Promise.all(
        Object.entries(changedTags).map(([teacherId, value]) =>
          updateEntityTag("teacher", teacherId, tagDefinition.id, value, userName)
        )
      );
      await fetchData();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setChangedTags({});
    setError(null);
    setSaveSuccess(false);
    setSearchTerm("");
    onHide();
  };

  const filteredTeachers = teachers
    .filter((t) => {
      const term = searchTerm.toLowerCase();
      return (
        t.name?.toLowerCase().includes(term) ||
        t.email?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Chatbot Access — Teachers
          <br />
          <small className="text-muted">{schoolName}</small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {saveSuccess && (
          <Alert variant="success" onClose={() => setSaveSuccess(false)} dismissible>
            Changes saved successfully
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p>Loading teachers...</p>
          </div>
        ) : teachers.length === 0 ? (
          <Alert variant="info">No teachers found for this school.</Alert>
        ) : (
          <>
            <Form.Control
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Email</th>
                  <th>Additional Roles</th>
                  <th className="text-center">Chatbot Access</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map((teacher) => {
                  const value = getCurrentValue(teacher.id);
                  return (
                    <tr key={teacher.id}>
                      <td>{teacher.name}</td>
                      <td className="text-muted">{teacher.email}</td>
                      <td className="text-muted">
                        {teacher.additionalroles
                          ? teacher.additionalroles
                          : "—"}
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="switch"
                          id={`chatbot-${teacher.id}`}
                          label={value === "ON" ? "Enabled" : "Disabled"}
                          checked={value === "ON"}
                          onChange={(e) => handleToggle(teacher.id, e.target.checked)}
                          disabled={saving}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || Object.keys(changedTags).length === 0 || loading}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            `Save Changes (${Object.keys(changedTags).length})`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TeacherChatbotAccessModal;
